/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { Box, Container, Grid, Paper, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ko';
import { ApplicantRecords, Applicant } from './Applicant';
import { DatePickerWrapper } from './DatePicker';
import { useAuth, UserProfile } from '../../services/cognito';
import { BaseAlertDialog } from './Dailogs';
import { useAxios } from '../../services/axios';
import config from '../../config';

dayjs.extend(utc);

export function CartWitnessingPage() {
  const { userProfile } = useAuth();
  const {
    response: applicantResponse,
    sendData: sendGetRequest,
    request,
  } = useAxios({
    baseURL: config.api.url,
    url: '/mdsouth/applicants',
    method: 'GET',
    params: { date: dayjs.utc().format('YYYY-MM-DD') },
  });

  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [date, setDate] = React.useState<Dayjs | null>(dayjs.utc().local());
  const [applicants, setApplicants] = React.useState<Applicant[]>([]);
  const [duplcatedAlertOpen, setDuplicatedAlertOpen] = React.useState(false);
  const [cancelAlertOpen, setCancelAlertOpen] = React.useState(false);
  const [notAllowedAlertOpen, setNotAllowedAlertOpen] = React.useState(false);
  const [targetId, setTargetId] = React.useState(0);

  const onDateChange = (newDate: Dayjs | null) => {
    setDate(newDate);
    sendGetRequest({ date: newDate?.format('YYYY-MM-DD') });
  };

  const onRegister = async () => {
    // check if the user is already registered
    if (applicants?.find(applicant => applicant.userId === profile?.id)) {
      setDuplicatedAlertOpen(true);
      return;
    }

    // send new applicant to server
    const created = dayjs.utc().toDate();

    const resp = await request({
      baseURL: config.api.url,
      url: '/mdsouth/applicants',
      method: 'POST',
      data: {
        date: date?.toDate(),
        userId: profile?.id,
        userName: profile?.fullKoreanName,
        created,
      },
    });

    // add new applicant by getting the highest id from applicants and add 1
    const newApplicant: Applicant = {
      id: resp.data.id,
      date: date?.toDate() as Date,
      userId: profile?.id || '',
      userName: profile?.fullKoreanName || '',
      created,
    };
    setApplicants([...applicants, newApplicant]);
  };

  const onDelete = (id: number, userId: string) => () => {
    if (profile?.id !== userId) {
      setNotAllowedAlertOpen(true);
      return;
    }
    setTargetId(id);
    setCancelAlertOpen(true);
  };

  const handleCancel = (status: string) => () => {
    if (status === 'yes') {
      request({
        baseURL: config.api.url,
        url: '/mdsouth/applicants',
        method: 'DELETE',
        params: { id: targetId },
      });
      setApplicants(applicants.filter(applicant => applicant.id !== targetId));
    }
    setCancelAlertOpen(false);
  };

  const isDisabledDate = (date: Dayjs) => {
    // disable all dates before today and it is not a Friday or Saturday
    // return date.isBefore(dayjs.utc().local(), 'day') || date.day() < 5;
    return date.day() < 5;
  };

  React.useEffect(() => {
    if (userProfile) {
      setProfile(userProfile);
    }
    if (applicantResponse !== undefined) {
      setApplicants(applicantResponse.data);
    }
  }, [userProfile, applicantResponse]);

  return (
    <Box
      component="main"
      sx={{
        backgroundColor: theme =>
          theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[900],
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
      }}
    >
      <Container
        disableGutters
        maxWidth="lg"
        sx={{ mt: 2, mb: 2, pl: 1, pr: 1 }}
      >
        <Grid container spacing={0.5}>
          <Grid item xs={12}>
            <Typography
              component="h2"
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ ml: 1 }}
            >
              전시대 봉사
            </Typography>
            <Paper
              sx={{ p: 2, display: 'flex', flexDirection: 'column' }}
              style={{ padding: 15 }}
            >
              <DatePickerWrapper
                date={date}
                onChange={onDateChange}
                shouldDisableDate={isDisabledDate}
              />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper
              sx={{ p: 2, display: 'flex', flexDirection: 'column' }}
              style={{ padding: 15 }}
            >
              <ApplicantRecords
                date={date}
                applicants={applicants}
                onRegister={onRegister}
                onDelete={onDelete}
              />
            </Paper>
          </Grid>
        </Grid>
        <BaseAlertDialog
          open={duplcatedAlertOpen}
          title="중복"
          message={`이미 ${date
            ?.locale('ko')
            .format('YYYY-MM-DD')}에 참여 신청하셨습니다`}
          onClose={() => () => setDuplicatedAlertOpen(false)}
        />
        <BaseAlertDialog
          open={cancelAlertOpen}
          title="확인"
          message="참여 신청을 정말 취소하시겠습니까?"
          onClose={handleCancel}
          activateYes
        />
        <BaseAlertDialog
          open={notAllowedAlertOpen}
          title="불가"
          message="다른 사람의 참여 신청은 취소할 수 없습니다"
          onClose={() => () => setNotAllowedAlertOpen(false)}
        />
      </Container>
    </Box>
  );
}
